import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ShoppingCart, FileText, Send, Clock,
  Package, Tag, Hash, DollarSign, MessageSquare
} from 'lucide-react';

const PurchaseRequestForm = () => {
  const [formData, setFormData] = useState({
    itemName: '',
    quantity: '',
    estimatedCost: '',
    vendor: '',
    priority: 'Normal',
    reason: '',
  });
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchRequestHistory = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/purchase-request/get-requests`);
      setRecentRequests(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch purchase request history:', error);
    }
  };

  useEffect(() => { fetchRequestHistory(); }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/purchase-request/request`,
        formData
      );
      if (response.data.success || response.status === 201) {
        setMessage({ type: 'success', text: 'Purchase Request submitted successfully!' });
        setFormData({ itemName: '', quantity: '', estimatedCost: '', vendor: '', priority: 'Normal', reason: '' });
        fetchRequestHistory();
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to submit request. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ', ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const priorityColor = (p) => {
    if (p === 'Urgent') return { bg: 'rgba(239,68,68,0.12)', color: '#F87171', border: 'rgba(239,68,68,0.25)' };
    if (p === 'High')   return { bg: 'rgba(245,158,11,0.12)', color: '#FBBF24', border: 'rgba(245,158,11,0.25)' };
    return { bg: 'rgba(59,130,246,0.1)', color: '#60A5FA', border: 'rgba(59,130,246,0.2)' };
  };

  // ─── Style tokens ───
  const card = { background: 'var(--nd-surface)', border: '1px solid var(--nd-w08)', borderRadius: 14, overflow: 'hidden' };
  const cardHeader = { padding: '18px 20px 14px', borderBottom: '1px solid var(--nd-w06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
  const cardBody = { padding: '18px 20px' };
  const label = { fontSize: 10, fontWeight: 700, color: 'var(--nd-tx40)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
  const inputBase = { width: '100%', height: 40, background: 'var(--nd-surface)', border: '1px solid var(--nd-w08)', borderRadius: 9, color: 'var(--nd-text)', fontSize: 13, paddingLeft: 36, paddingRight: 12, outline: 'none', fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box' };
  const iconWrap = { position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex' };
  const iconPillOrange = { width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)' };
  const iconPillBlue   = { width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.2)' };

  const badge = (status) => {
    const map = {
      Approved: { bg: 'rgba(34,211,165,0.1)', color: '#22D3A5', border: 'rgba(34,211,165,0.25)' },
      Rejected: { bg: 'rgba(248,113,113,0.1)', color: '#F87171', border: 'rgba(248,113,113,0.25)' },
    };
    const s = map[status] || { bg: 'rgba(245,158,11,0.1)', color: '#FBBF24', border: 'rgba(245,158,11,0.25)' };
    return { display: 'inline-block', padding: '3px 8px', borderRadius: 5, fontSize: 9, fontWeight: 800, letterSpacing: '0.07em', background: s.bg, color: s.color, border: `1px solid ${s.border}` };
  };

  return (
    <div style={{ padding: '24px 16px', maxWidth: 960, margin: '0 auto', fontFamily: "'DM Sans', sans-serif" }}>

      {/* Page heading */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--nd-tx35)', textTransform: 'uppercase' }}>Operations</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--nd-text)', marginTop: 2, letterSpacing: '-0.02em' }}>Purchase Request Portal</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>

        {/* ─── FORM ─── */}
        <div style={card}>
          <div style={cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={iconPillOrange}><ShoppingCart size={16} color="#F59E0B" /></div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--nd-tx35)', textTransform: 'uppercase' }}>New Request</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--nd-text)', marginTop: 1 }}>Submit Purchase Request</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--nd-tx30)' }}>
              <span style={{ color: '#F59E0B', fontWeight: 700 }}>● </span>Live
            </div>
          </div>

          <div style={cardBody}>
            {message.text && (
              <div style={{ padding: '9px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, marginBottom: 14, background: message.type === 'success' ? 'rgba(34,211,165,0.1)' : 'rgba(239,68,68,0.1)', color: message.type === 'success' ? '#22D3A5' : '#EF4444', border: `1px solid ${message.type === 'success' ? 'rgba(34,211,165,0.25)' : 'rgba(239,68,68,0.25)'}` }}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>

              {/* Row 1: Item Name + Priority */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div>
                  <div style={label}>Item / Material</div>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Package size={14} color="var(--nd-tx30)" /></span>
                    <input type="text" name="itemName" placeholder="e.g. Safety helmets" style={inputBase} value={formData.itemName} onChange={handleChange} required />
                  </div>
                </div>
                <div>
                  <div style={label}>Priority</div>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Tag size={14} color="var(--nd-tx30)" /></span>
                    <select name="priority" style={{ ...inputBase, cursor: 'pointer', WebkitAppearance: 'none' }} value={formData.priority} onChange={handleChange}>
                      {['Normal', 'High', 'Urgent'].map(p => <option key={p} value={p} style={{ background: 'var(--nd-surface-3)' }}>{p}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ height: 1, background: 'var(--nd-w05)', margin: '4px 0 14px' }} />

              {/* Row 2: Quantity + Cost */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div>
                  <div style={label}>Quantity</div>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Hash size={14} color="var(--nd-tx30)" /></span>
                    <input type="number" name="quantity" placeholder="0" style={{ ...inputBase, fontFamily: "'DM Mono', monospace" }} value={formData.quantity} onChange={handleChange} required />
                  </div>
                </div>
                <div>
                  <div style={label}>Estimated Cost (PKR)</div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ ...iconWrap, color: '#F59E0B', fontWeight: 800, fontSize: 15, fontFamily: 'monospace' }}>₨</span>
                    <input type="number" name="estimatedCost" placeholder="0.00" style={{ ...inputBase, fontFamily: "'DM Mono', monospace", fontSize: 14 }} value={formData.estimatedCost} onChange={handleChange} required />
                  </div>
                </div>
              </div>

              {/* Vendor */}
              <div style={{ marginBottom: 14 }}>
                <div style={label}>Vendor / Supplier <span style={{ color: 'var(--nd-tx25)', fontSize: 9 }}>optional</span></div>
                <div style={{ position: 'relative' }}>
                  <span style={iconWrap}><DollarSign size={14} color="var(--nd-tx30)" /></span>
                  <input type="text" name="vendor" placeholder="Vendor name..." style={inputBase} value={formData.vendor} onChange={handleChange} />
                </div>
              </div>

              {/* Reason */}
              <div style={{ marginBottom: 20 }}>
                <div style={label}>Reason / justification <span style={{ color: 'var(--nd-tx25)', fontSize: 9 }}>optional</span></div>
                <div style={{ position: 'relative' }}>
                  <span style={{ ...iconWrap, top: 13, transform: 'none' }}><FileText size={14} color="var(--nd-tx30)" /></span>
                  <textarea name="reason" placeholder="Why is this purchase needed..." style={{ ...inputBase, height: 72, paddingTop: 10, resize: 'vertical', lineHeight: 1.5 }} value={formData.reason} onChange={handleChange} />
                </div>
              </div>

              <button type="submit" disabled={loading} style={{ width: '100%', height: 42, borderRadius: 10, background: loading ? 'rgba(245,158,11,0.4)' : '#F59E0B', border: 'none', color: 'var(--nd-surface)', fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                {loading ? 'Submitting...' : <><Send size={14} /> Submit Purchase Request</>}
              </button>
            </form>
          </div>
        </div>

        {/* ─── HISTORY ─── */}
        <div style={card}>
          <div style={cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={iconPillBlue}><Clock size={16} color="#60A5FA" /></div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--nd-tx35)', textTransform: 'uppercase' }}>History</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--nd-text)', marginTop: 1 }}>My Requests</div>
              </div>
            </div>
            <div style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, color: '#60A5FA' }}>
              {recentRequests.length} total
            </div>
          </div>

          <div style={{ ...cardBody, paddingTop: 4, maxHeight: 520, overflowY: 'auto' }}>
            {recentRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--nd-tx30)', fontSize: 12, border: '1px dashed var(--nd-w08)', borderRadius: 10, marginTop: 8 }}>
                No requests found.
              </div>
            ) : recentRequests.map((req) => {
              const pc = priorityColor(req.priority);
              return (
                <div key={req.id} style={{ padding: '13px 0', borderBottom: '1px solid var(--nd-w05)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--nd-text)', marginBottom: 3 }}>{req.item_name}</div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4, background: pc.bg, color: pc.color, border: `1px solid ${pc.border}` }}>{req.priority || 'NORMAL'}</span>
                      <span style={{ fontSize: 11, color: 'var(--nd-tx40)' }}>Qty: {req.quantity}</span>
                      {req.vendor && <span style={{ fontSize: 11, color: 'var(--nd-tx35)' }}>· {req.vendor}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--nd-tx30)', marginBottom: 4 }}>{formatTime(req.requested_at)}</div>
                    {req.reason && (
                      <div style={{ fontSize: 11, color: 'var(--nd-tx35)', fontStyle: 'italic' }}>"{req.reason}"</div>
                    )}
                    {/* Finance comment visible to ops */}
                    {req.finance_comment && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#60A5FA', background: 'rgba(59,130,246,0.07)', borderRadius: 6, padding: '5px 9px', border: '1px solid rgba(59,130,246,0.15)', display: 'flex', gap: 5, alignItems: 'flex-start' }}>
                        <MessageSquare size={11} style={{ marginTop: 1, flexShrink: 0 }} />
                        <span>Finance: "{req.finance_comment}"</span>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#F59E0B', fontFamily: "'DM Mono', monospace", marginBottom: 5 }}>₨{req.estimated_cost}</div>
                    <span style={badge(req.status)}>{req.status ? req.status.toUpperCase() : 'PENDING'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PurchaseRequestForm;